import { FC, useEffect, useRef, useCallback } from 'react';
import { extractVideoId } from '@/utils/youtube';

interface YouTubePlayerProps {
  videoUrl: string;
  onProgressUpdate: (percent: number) => void;
}

declare global {
  interface Window {
    YT: {
      Player: new (
        elementId: string,
        config: {
          videoId: string;
          playerVars?: Record<string, number | string>;
          events?: {
            onReady?: (event: { target: YTPlayer }) => void;
            onStateChange?: (event: { data: number; target: YTPlayer }) => void;
          };
        }
      ) => YTPlayer;
      PlayerState: {
        PLAYING: number;
        PAUSED: number;
        ENDED: number;
      };
    };
    onYouTubeIframeAPIReady?: () => void;
  }
}

interface YTPlayer {
  getCurrentTime: () => number;
  getDuration: () => number;
  destroy: () => void;
}

function loadYouTubeAPI(): Promise<void> {
  return new Promise((resolve) => {
    if (window.YT?.Player) {
      resolve();
      return;
    }
    const existing = document.getElementById('youtube-iframe-api');
    if (existing) {
      const check = setInterval(() => {
        if (window.YT?.Player) {
          clearInterval(check);
          resolve();
        }
      }, 100);
      return;
    }
    window.onYouTubeIframeAPIReady = () => resolve();
    const script = document.createElement('script');
    script.id = 'youtube-iframe-api';
    script.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(script);
  });
}

const YouTubePlayer: FC<YouTubePlayerProps> = ({ videoUrl, onProgressUpdate }) => {
  const playerRef = useRef<YTPlayer | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const containerId = useRef(`yt-player-${Math.random().toString(36).slice(2, 9)}`);

  const videoId = extractVideoId(videoUrl);

  const trackProgress = useCallback(() => {
    if (!playerRef.current) return;
    try {
      const current = playerRef.current.getCurrentTime();
      const duration = playerRef.current.getDuration();
      if (duration > 0) {
        onProgressUpdate(Math.round((current / duration) * 100));
      }
    } catch {
      // Player may not be ready
    }
  }, [onProgressUpdate]);

  useEffect(() => {
    if (!videoId) return;

    let destroyed = false;

    const init = async () => {
      await loadYouTubeAPI();
      if (destroyed) return;

      playerRef.current = new window.YT.Player(containerId.current, {
        videoId,
        playerVars: { rel: 0, modestbranding: 1 },
        events: {
          onStateChange: (event) => {
            if (event.data === window.YT.PlayerState.PLAYING) {
              if (!intervalRef.current) {
                intervalRef.current = setInterval(trackProgress, 5000);
              }
            } else {
              if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
              }
              trackProgress();
            }
          },
        },
      });
    };

    init();

    return () => {
      destroyed = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
      try {
        playerRef.current?.destroy();
      } catch {
        // ignore
      }
    };
  }, [videoId, trackProgress]);

  if (!videoId) {
    return (
      <div className="aspect-video bg-surface-card rounded-xl flex items-center justify-center">
        <p className="text-content-muted">URL de vídeo inválida</p>
      </div>
    );
  }

  return (
    <div className="aspect-video rounded-xl overflow-hidden bg-black" data-testid="youtube-player">
      <div id={containerId.current} className="w-full h-full" />
    </div>
  );
};

export default YouTubePlayer;
